import { useQueries } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { isWaitingKind, type WaitingKind } from '../utils/waiting.js';
import type { HiddenInboxItem, InboxItem } from '../api/types.js';
import { DecisionList } from '../components/inbox.js';
import { useApiClient } from '../hooks/use-api-client.js';
import { useServers } from '../hooks/use-servers.js';
import { useInboxes } from '../hooks/use-inbox.js';

// To do (ADR-039 section 6): the home page, what waits on the operator across servers in one ranked list. It reads the
// inboxes the server pages and the app badge read (same queries), so there is one count; each line says its server.

/** Decisions shown before « Show more »: more than on a server's page, this is where deciding happens */
export const TODO_FIRST = 10;

/** Ranked as each server ranks its own: the score, then the longest wait */
function ranked(items: InboxItem[]): InboxItem[] {
  return [...items].sort((a, b) => b.score - a.score || Date.parse(a.since) - Date.parse(b.since));
}

export function Todo() {
  const api = useApiClient();
  const [params, setParams] = useSearchParams();
  const waiting = params.get('waiting');
  const filter = {
    server: params.get('server'), waiting: isWaitingKind(waiting) ? waiting : null,
    onChange: (f: { server: string | null; waiting: WaitingKind | null }) => setParams((now) => {
      const next = new URLSearchParams(now);
      for (const [k, v] of [['server', f.server], ['waiting', f.waiting]] as const) if (v) next.set(k, v); else next.delete(k);
      return next;
    }, { replace: true }),
  };
  const { data: servers, isLoading, error } = useServers();
  const list = servers ?? [];
  const inboxes = useInboxes(list.map((s) => s.id));
  const hiddens = useQueries({ queries: list.map((s) => ({ queryKey: ['inbox-hidden', s.id], queryFn: () => api.inbox.hidden(s.id), refetchInterval: 300_000 })) });
  const tag = <T extends InboxItem>(items: T[] | undefined, i: number): T[] => (items ?? []).map((item) => ({ ...item, server: { id: list[i].id, name: list[i].name } }));
  const data = ranked(inboxes.flatMap((q, i) => tag(q.data, i)));
  const hidden = hiddens.flatMap((q, i) => tag(q.data, i)) as HiddenInboxItem[];
  const unread = list.filter((_, i) => inboxes[i]?.isError).map((s) => s.name);
  const loading = isLoading || inboxes.some((q) => q.isLoading);

  if (error) {
    return <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Servers not read: {error.message}</p>;
  }
  if (loading && !data.length) {
    return (
      <div>
        <h1 className="page-title text-2xl">To do</h1>
        <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-3)' }}>Reading what waits on you…</p>
      </div>
    );
  }
  if (!list.length) {
    return (
      <div>
        <h1 className="page-title text-2xl">To do</h1>
        <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-3)' }}>No server is set up for your organisation yet.</p>
      </div>
    );
  }
  return <DecisionList data={data} hidden={hidden} title="To do" first={TODO_FIRST} page unread={unread} filter={filter} />;
}
